// NodelistField.js
//
// Helpers for Relation fields (canonical type `relation`, legacy `nodelist`),
// including single-select mode.

import { isEnumCompatibleFieldType, isRelationFieldType } from 'utilities/FieldTypes';

/**
 * Returns whether a Relation field allows multiple related records.
 * Missing/undefined means multi (legacy default).
 *
 * @param {object|null|undefined} field
 * @returns {boolean}
 */
export function isNodelistAllowMultiple(field) {
  if (!field || typeof field !== 'object') return true;
  return field.allowMultiple !== false;
}

/**
 * Returns whether a nodelist field is single-select.
 *
 * @param {object|null|undefined} field
 * @returns {boolean}
 */
export function isNodelistSingleSelect(field) {
  return isRelationFieldType(field)
    && !isNodelistAllowMultiple(field);
}

/**
 * Normalizes a nodelist field value to a unique list of node keys.
 *
 * @param {unknown} value
 * @returns {string[]}
 */
export function normalizeNodelistKeys(value) {
  const list = Array.isArray(value) ? value : value == null ? [] : [value];
  const seen = new Set();

  return list
    .map((entry) => {
      if (typeof entry === 'string') return entry.trim();
      if (entry && typeof entry === 'object') {
        return String(entry?.key || '').trim();
      }
      return '';
    })
    .filter((entry) => {
      if (!entry || seen.has(entry)) return false;
      seen.add(entry);
      return true;
    });
}

/**
 * Returns the primary (first) related node key, or ''.
 *
 * @param {unknown} value
 * @returns {string}
 */
export function getNodelistPrimaryKey(value) {
  return normalizeNodelistKeys(value)[0] || '';
}

/**
 * Normalizes a nodelist value, optionally enforcing single-select cardinality.
 *
 * @param {unknown} value
 * @param {{ allowMultiple?: boolean }} [options]
 * @returns {string[]}
 */
export function normalizeNodelistValue(value, { allowMultiple = true } = {}) {
  const keys = normalizeNodelistKeys(value);
  if (allowMultiple) return keys;
  return keys.length ? [keys[0]] : [];
}

/**
 * Builds a display title for a related node.
 *
 * @param {object|null|undefined} node
 * @returns {string}
 */
export function getNodelistNodeTitle(node) {
  return String(node?.title || '').trim() || 'Untitled';
}

/**
 * Whether a field can be used as a board grouping dimension.
 * Single-select Relation behave like Select for grouping.
 *
 * @param {object|null|undefined} field
 * @returns {boolean}
 */
export function isBoardGroupableField(field) {
  if (isEnumCompatibleFieldType(field)) return true;
  return isNodelistSingleSelect(field);
}
